function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length >= 2) {
    return parts.pop().split(';').shift();
  }

  return null;
}

function setCookie(name, value, days) {
  let expires = '';
  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = `; expires=${date.toUTCString()}`;
  }
  const cookieDomain = window.WE_COOKIE_DOMAIN || COOKIE_DOMAIN;
  document.cookie = `${name}=${value || ''}${expires};${
    cookieDomain !== 'localhost' ? `domain=${cookieDomain};` : ''
  }path=/`;
}

function deleteCookie(name) {
  document.cookie = `${name}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
}

const defaultImplementations = () => {
  const DATA_URL = window.WE_COOKIECONSENT_DATAURL || COOKIECONSENT_DATAURL;
  return {
    cookie: {
      get: getCookie,
      set: setCookie,
      delete: deleteCookie,
    },
    client: {
      get: () =>
        new Promise((resolve, reject) => {
          const oReq = new XMLHttpRequest();
          // Can't use arrow function because this has a different context

          oReq.onreadystatechange = function () {
            if (this.readyState === 4 && this.status === 200) {
              resolve(JSON.parse(this.responseText));
            } else if (this.readyState === 4 && this.status >= 400) {
              reject(new Error('could not fetch cookie data'));
            }
          };
          oReq.open('GET', DATA_URL);
          oReq.send();
        }),
    },
  };
};

/**
 * @example
 * // instantiate service
 * const cookieConsent = new CookieConsentService();
 * // initialise - returns groups array with consentinformation
 * const groups = cookieConsent.initialise()
 */
class CookieConsentService {
  constructor(implementations) {
    this.namespace = 'we.cookie-consent';

    this.version = null;
    this.implementations = { ...defaultImplementations(), ...implementations };

    this.saved = this.getSaved();
    this.groups = [];
    this.dialog = null;
    this.closeCallback = null;
    this.visibleGroup = null;
  }

  setDialog(dialog) {
    this.dialog = dialog;
  }

  onDialogClose(callback) {
    this.closeCallback = callback;
  }

  openDialog() {
    this.reset();
    this.dialog.show();
  }

  execDialogCloseCallback() {
    if (this.closeCallback) {
      this.closeCallback();
    }
  }

  /**
   * Get cookie consent data from cookie
   */
  getSaved() {
    const savedCookie = this.implementations.cookie.get(this.namespace);
    if (!savedCookie) {
      return [];
    }
    const [version = null, consents = null] = savedCookie.split('::') || [];
    if (!version) {
      return [];
    }
    this.acceptedVersion = version;

    return consents.split(',');
  }

  /**
   * Merges visual data with user consent
   *
   * @returns group Array
   */
  mergeConsent(groups) {
    return groups.map(group => ({
      ...group,
      services: group.services.map(service => ({
        ...service,
        accepted: this.saved.includes(service.id),
      })),
    }));
  }

  /**
   * Fetches representational Data from the API
   */
  async initialise() {
    const {
      data: { dependencies, services },
      meta: { version },
    } = await this.implementations.client.get();
    this.dependencies = dependencies;
    this.groups = services.map(item => ({
      ...item,
      services: item.services.map(service => ({
        ...service,
        dependencies:
          service.dependencies &&
          service.dependencies.map(dependency =>
            this.dependencies.find(dep => dep.id === dependency),
          ),
      })),
    }));
    this.version = version;
    this.saved = this.getSaved();
    return this.mergeConsent(this.groups);
  }

  setVisibleServiceGroup(id) {
    this.visibleGroup = id;
  }

  // find group id that this service belongs to (to open disclosure for this group when consent is required for a particular service (google maps etc.) and requested again)
  getServiceGroup(services) {
    const serviceGroup = this.groups?.find(c =>
      c.services?.find(s => s.id === services[0]),
    )?.id;
    return serviceGroup;
  }

  saveCookie() {
    let expiresIn = 30;
    if (this.saved && this.saved.length > 0 && this.hasPartialConsent()) {
      expiresIn = 182;
    }
    if (this.saved && this.saved.length > 0 && !this.hasPartialConsent()) {
      expiresIn = 365;
    }

    this.implementations.cookie.set(
      this.namespace,
      `${this.version}::${this.saved.join(',')}`,
      expiresIn,
    );
  }

  updateAll(items) {
    this.saved = [];
    items.forEach(item => this.updateSingle(item, true));
    return this;
  }

  getVersion() {
    return this.version;
  }

  getService(id) {
    let service = null;

    this.groups.forEach(group => {
      if (!service) {
        service = group.services.find(srv => srv.id === id);
      }
    });
    return service;
  }

  hasConsent(idArray) {
    return idArray.filter(x => !this.saved.includes(x)).length === 0;
  }

  /**
   * Update single cookie consent without clearing the storage
   */
  updateSingle(id, accepted) {
    const service = this.getService(id);
    if (accepted) {
      this.saved = [...this.saved, id];
      if (service?.dependencies) {
        this.saved = [...this.saved];
        service.dependencies.forEach(dep => {
          if (this.saved.includes(dep.id)) {
            return;
          }
          this.saved = [...this.saved, dep.id];
        });
      }
    }
    return this;
  }

  clearCookie() {
    this.implementations.cookie.delete(this.namespace);
  }

  persist() {
    this.clearCookie();
    this.saveCookie();

    return this;
  }

  toggleGroup(id, accepted) {
    const servicesFromGroup = this.groups
      .find(item => item.id === id)
      ?.services.map(item => item.id);
    servicesFromGroup.forEach(serviceId => {
      this.updateSingle(serviceId, accepted);
    });
    return this;
  }

  hasPartialConsent() {
    let services = [];
    const dependencies = {};
    this.groups.forEach(item => {
      services = [
        ...services,
        ...item.services.map(service => {
          service.dependencies.forEach(dependency => {
            dependencies[dependency] = true;
          });
          return service.id;
        }),
      ];
    });

    const values = Object.keys(dependencies).map(k => dependencies[k]);
    return this.saved && this.saved.length !== services.length + values.length;
  }

  hasCookieConsent() {
    return this.implementations.cookie.get(this.namespace) !== null;
  }

  hasAcceptedCurrentVersion() {
    return this.acceptedVersion === this.getVersion();
  }

  accept(id) {
    this.updateSingle(id, true);
    return this;
  }

  acceptAll() {
    this.reset();
    this.groups.forEach(item =>
      item.services.forEach(({ id }) => {
        this.accept(id);
      }),
    );
    return this;
  }

  refuse(id) {
    this.updateSingle(id, false);
    return this;
  }

  reset() {
    this.saved = [];
    return this;
  }

  getAccepted() {
    // remove empty strings from array
    return this.saved;
  }

  getUsed() {
    return this.getServices(s => s.accepted);
  }

  getUnused() {
    const used = this.getUsed();
    const unused = this.getServices(s => !s.accepted);

    return unused.filter(s => !used.includes(s));
  }

  getServices(filterFunc = () => true) {
    const uniq = (x, i, a) => a.indexOf(x) === i;

    return this.getGroups().reduce((accu, cat) => {
      const services = cat.services.filter(filterFunc).map(s => {
        let ids = [s.id];
        if (s.dependencies) {
          ids = [...ids, ...s.dependencies.map(dep => dep.id)];
        }

        return ids;
      });

      const flatServices = services.reduce((acc, val) => acc.concat(val), []);

      return [...accu, ...flatServices].filter(uniq);
    }, []);
  }

  reRequestConsent() {
    if (!this.hasAcceptedCurrentVersion()) {
      // We have to clear saved since its saved from the initialisation method
      // otherwise old saved services will get concatenanted by updateSingle() and updateAll() uses updateSingle()
      this.reset();
      return true;
    }

    return false;
  }

  /**
   * Merges the groups with the user consent data
   */
  getGroups() {
    return this.mergeConsent(this.groups);
  }
}

export default CookieConsentService;
